import { useFormik } from 'formik';
import { Form } from 'react-bootstrap';
import * as Yup from 'yup';
import { Modal, TextInput } from '@pmi/dsm-react';
import { AgreementApiModel } from '../../../../shared/models/DealModel';
import styles from './editAgreementNameModal.module.scss';
import { useUpdateDealNameMutation } from './apiSlice';
import { toastWithAnalytics } from '../../../../shared/analytics';

interface EditAgreementNameModalProps {
  accountId: string;
  deal: AgreementApiModel;
  onCancel?: () => void;
}

export default function EditAgreementNameModal({
  accountId,
  deal,
  onCancel = () => {},
}: EditAgreementNameModalProps) {
  const [updateDealName, updateDealNameStatus] = useUpdateDealNameMutation();

  const initialData = {
    dealName: deal.dealName,
  };
  const validationSchema = Yup.object().shape({
    dealName: Yup.string().max(40, 'Agreement Name must be max 40 characters'),
  });

  const onSave = async (model: { dealName: string }) => {
    updateDealName({
      accountId,
      dealId: deal.id,
      name: model.dealName,
    })
      .unwrap()
      .then(() => {
        toastWithAnalytics(`Agreement Name updated successfully`, 'success');
      })
      .catch((ex) => {
        console.log(ex);
        toastWithAnalytics(
          `Error while updating Agreement !! ${ex?.data?.detail}.`,
          'danger',
        );
      });
  };

  const onCancelHandler = () => {
    onCancel();
  };

  const formik = useFormik({
    initialValues: initialData,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values: { dealName: string }) => {
      onSave(values);
    },
    validationSchema,
  });

  const onUpdateClick = () => {
    formik.submitForm();
  };

  return (
    <Modal
      show
      customClassName="modalBody100"
      onClose={onCancelHandler}
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: onCancelHandler,
          disabled: updateDealNameStatus.isLoading,
        },
        {
          titleText: 'Save Changes',
          variant: 'primary',
          onClick: onUpdateClick,
          disabled: updateDealNameStatus.isLoading,
        },
      ]}
    >
      <div className="d-flex flex-column">
        <div className={`mb-3 ${styles.titleBlock}`}>
          <h5 className="mb-2">Edit Agreement</h5>
          <span>ID: {deal.id}</span>
        </div>
        <Form className="mb-4" onSubmit={formik.handleSubmit}>
          <div className="row gy-3 row-cols-1 mb-2">
            <div className="col" data-testid="deal_name_container_testid">
              <Form.Group
                controlId="dealName"
                className={`${formik.errors.dealName ? 'is-invalid' : ''}`}
              >
                <Form.Label>Agreement Name</Form.Label>
                <TextInput
                  name="dealName"
                  value={formik.values.dealName}
                  className={`${formik.errors.dealName ? 'is-invalid' : ''}`}
                  onChange={formik.handleChange}
                  isInvalid={!!formik.errors.dealName}
                  validationMessage={formik.errors.dealName}
                />
              </Form.Group>
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
